/**
 * Permission Group Form Modal
 * Phase 5: RBAC & Permission System - M4
 * 
 * Create/Edit permission group with tree view selector
 * Features: Hierarchical permission tree, checkboxes, search
 */

import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Typography,
  Box,
  Alert,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  Chip,
  CircularProgress
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import permissionService from '../../services/permissionService';
import {
  createPermissionGroup,
  updatePermissionGroup
} from '../../redux/slices/permissionGroupSlice';

const PermissionGroupFormModal = ({ open, onClose, group }) => {
  const dispatch = useDispatch();
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    permissionIds: []
  });
  
  const [errors, setErrors] = useState({});
  const [availablePermissions, setAvailablePermissions] = useState([]);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [expandedItems, setExpandedItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [filteredOutPermissions, setFilteredOutPermissions] = useState([]);
  
  // Fetch available permissions on mount
  useEffect(() => {
    if (open) {
      fetchAvailablePermissions();
    }
  }, [open]);
  
  // Initialize form data (only after available permissions are fetched)
  useEffect(() => {
    if (open && !loading) {
      if (group && availablePermissions.length > 0) {
        // Filter group permissions to only include available ones
        // This prevents selecting permissions that user no longer has access to
        const availablePermissionIds = new Set(availablePermissions.map(p => p._id));
        const allGroupPermissionIds = group.permissions?.map(p => p._id) || [];
        const validGroupPermissions = allGroupPermissionIds
          .filter(id => availablePermissionIds.has(id));
        const filteredOut = allGroupPermissionIds
          .filter(id => !availablePermissionIds.has(id));
        
        setFormData({
          name: group.name || '',
          description: group.description || '',
          permissionIds: validGroupPermissions
        });
        setSelectedPermissions(validGroupPermissions);
        setFilteredOutPermissions(filteredOut);
      } else if (group) {
        // Group exists but permissions not loaded yet - set basic info only
        setFormData({
          name: group.name || '',
          description: group.description || '',
          permissionIds: []
        });
        setSelectedPermissions([]);
        setFilteredOutPermissions([]);
      } else {
        // New group
        setFormData({
          name: '',
          description: '',
          permissionIds: []
        });
        setSelectedPermissions([]);
        setFilteredOutPermissions([]);
      }
      setErrors({});
      setSearchTerm('');
    }
  }, [group, open, loading, availablePermissions]);
  
  const fetchAvailablePermissions = async () => {
    try {
      setLoading(true);
      const data = await permissionService.getAvailablePermissions();
      // Backend returns { tree, flat } - we need the flat array
      setAvailablePermissions(data?.flat || []);
    } catch (error) {
      console.error('Failed to fetch permissions:', error);
      setAvailablePermissions([]); // Ensure it's always an array
    } finally {
      setLoading(false);
    }
  };
  
  // Filter permissions by search
  const filteredPermissions = availablePermissions.filter(p =>
    p.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  // Toggle permission selection
  const togglePermission = (permissionId) => {
    setSelectedPermissions(prev => {
      if (prev.includes(permissionId)) {
        return prev.filter(id => id !== permissionId);
      } else {
        return [...prev, permissionId];
      }
    });
  };
  
  // Select all filtered permissions
  const handleSelectAll = () => {
    const allFilteredIds = filteredPermissions.map(p => p._id);
    setSelectedPermissions(prev => {
      const newSet = new Set([...prev, ...allFilteredIds]);
      return Array.from(newSet);
    });
  };
  
  // Clear all selections
  const handleClearAll = () => {
    setSelectedPermissions([]);
  };
  
  // Validation
  const validate = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Grup adı gereklidir';
    }
    
    if (selectedPermissions.length === 0) {
      newErrors.permissions = 'En az bir yetki seçmelisiniz';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Handle submit
  const handleSubmit = async () => {
    if (!validate()) return;
    
    const submitData = {
      name: formData.name.trim(),
      description: formData.description.trim() || null,
      permissionIds: selectedPermissions
    };
    
    if (group) {
      await dispatch(updatePermissionGroup({
        groupId: group._id,
        updates: submitData
      }));
    } else {
      await dispatch(createPermissionGroup(submitData));
    }
    
    onClose();
  };
  
  // Group permissions by category (using code prefix)
  const groupedPermissions = filteredPermissions.reduce((acc, permission) => {
    const category = permission.code.split('.')[0];
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(permission);
    return acc;
  }, {});
  
  // Get category label
  const getCategoryLabel = (category) => {
    const labels = {
      'users': '👥 Kullanıcı Yönetimi',
      'company': '🏢 Şirket Yönetimi',
      'documents': '📄 Doküman Yönetimi',
      'groups': '👥 Grup Yönetimi',
      'invites': '✉️ Davet Yönetimi',
      'system': '⚙️ Sistem Yönetimi'
    };
    return labels[category] || `📋 ${category.charAt(0).toUpperCase() + category.slice(1)}`;
  };
  
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {group ? '✏️ Grup Düzenle' : '➕ Yeni Grup Oluştur'}
      </DialogTitle>
      
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {/* Alert */}
          <Alert severity="info" sx={{ fontSize: '0.875rem' }}>
            <strong>Not:</strong> Bu gruba atanacak kullanıcılar, sadece seçtiğiniz yetkilere sahip olacaklardır.
          </Alert>
          
          {/* Warning: Filtered out permissions */}
          {filteredOutPermissions.length > 0 && (
            <Alert severity="warning" sx={{ fontSize: '0.875rem' }}>
              <strong>⚠️ Uyarı:</strong> Bu grubun {filteredOutPermissions.length} yetkisi artık erişebileceğiniz yetkiler arasında değil ve otomatik olarak kaldırıldı. 
              Grubu güncellemek için sadece erişebileceğiniz yetkileri seçebilirsiniz.
            </Alert>
          )}
          
          {/* Group Name */}
          <TextField
            label="Grup Adı *"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            error={!!errors.name}
            helperText={errors.name || 'Örnek: Satış Ekibi, Muhasebe Departmanı'}
            fullWidth
          />
          
          {/* Description */}
          <TextField
            label="Açıklama"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            multiline
            rows={2}
            helperText="Bu grubun amacını kısaca açıklayın"
            fullWidth
          />
          
          {/* Permission Selection Header */}
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                Yetkiler * ({selectedPermissions.length} seçili)
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button size="small" onClick={handleSelectAll} variant="outlined">
                  Tümünü Seç
                </Button>
                <Button size="small" onClick={handleClearAll} variant="outlined" color="error">
                  Tümünü Kaldır
                </Button>
              </Stack>
            </Box>
            
            {errors.permissions && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {errors.permissions}
              </Alert>
            )}
            
            {/* Search */}
            <TextField
              placeholder="Yetki ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              fullWidth
              size="small"
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
            
            {/* Loading */}
            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            )}
            
            {/* Permission Tree */}
            {!loading && (
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 2,
                  maxHeight: 400,
                  overflow: 'auto',
                  bgcolor: 'background.paper'
                }}
              >
                <SimpleTreeView
                  expandedItems={expandedItems}
                  onExpandedItemsChange={(event, itemIds) => setExpandedItems(itemIds)}
                >
                  {Object.entries(groupedPermissions).map(([category, permissions]) => (
                    <TreeItem
                      key={category}
                      itemId={category}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', py: 0.5 }}>
                          <Typography variant="subtitle2" fontWeight="bold">
                            {getCategoryLabel(category)}
                          </Typography>
                          <Chip 
                            label={permissions.length}
                            size="small"
                            sx={{ ml: 1, height: 20 }}
                          />
                        </Box>
                      }
                    >
                      {permissions.map(permission => (
                        <TreeItem
                          key={permission._id}
                          itemId={permission._id}
                          label={
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={selectedPermissions.includes(permission._id)}
                                  onChange={() => togglePermission(permission._id)}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              }
                              label={
                                <Box>
                                  <Typography variant="body2">
                                    {permission.name}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {permission.code}
                                  </Typography>
                                </Box>
                              }
                              sx={{ width: '100%', m: 0 }}
                            />
                          }
                        />
                      ))}
                    </TreeItem>
                  ))}
                </SimpleTreeView>
                
                {/* Empty State */}
                {Object.keys(groupedPermissions).length === 0 && (
                  <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
                    {searchTerm ? 'Arama sonucu bulunamadı' : 'Henüz yetki bulunamadı'}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </Stack>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit">
          İptal
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={loading}
        >
          {group ? 'Güncelle' : 'Oluştur'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

PermissionGroupFormModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  group: PropTypes.object
};

export default PermissionGroupFormModal;

